import React from 'react';
import { Link } from 'gatsby';
import Layout from '../components/Layout';
import Section from '../components/Section';
import { Container, Row, Column } from '../components/Grid';
import Image from '../components/Image';
import Button from '../components/Button';
import Video from '../components/Video';
import AboutPartners from '../components/AboutPartners';
import './index.scss';

const IndexPage = () => (
  <Layout title="Teaching With Testimony">

    {/* ── Hero Mosaic (3 cols × 2 rows, columns: 2fr 1fr 2fr) ──── */}
    {/* Row 1: EDUCATE | center-photo | EMPOWER                   */}
    {/* Row 2: photo   | INSPIRE      | photo                     */}
    <div className="hero-mosaic">
      <div className="mosaic-tile mosaic-photo">
        <Image filename="educate.png" alt="Educate" />
      </div>
      <div className="mosaic-tile mosaic-photo">
        <Image filename="hero-Shoah-Hero-Size-1-A.png" alt="A student engaged in learning" />
      </div>
      <div className="mosaic-tile mosaic-photo">
        <Image filename="empower.png" alt="Empower" />
      </div>
      <div className="mosaic-tile mosaic-photo">
        <Image filename="hero-Shoah-Hero-Size-2-A.png" alt="A Holocaust survivor sharing their testimony" />
      </div>
      <div className="mosaic-tile mosaic-photo">
        <Image filename="inspire.png" alt="Inspire" />
      </div>
      <div className="mosaic-tile mosaic-photo">
        <Image filename="hero-Shoah-Hero-Size-1-B.png" alt="Students learning through testimony" />
      </div>
    </div>

    {/* ── Featured callout cards ──────────────────────────────────── */}
    <Section>
      <Container>
        <Row>
          <Column size={6}>
            <div className="home-callout-card">
              <p>
                Explore the New Video Topic Series:{' '}
                <em>The Art of Resilience</em> For Elementary Students
              </p>
              <Link to="/video-topic-series/" className="home-callout-card__arrow" aria-label="Explore the Video Topic Series: The Art of Resilience">→</Link>
            </div>
          </Column>
          <Column size={6}>
            <div className="home-callout-card">
              <p>
                Explore what it means to have the courage to be kind in our NEW
                Virtual Field Trip{' '}
                <em>White Bird - The Courage of Kindness</em>
              </p>
              <Link to="/virtual-field-trip/white-bird/" className="home-callout-card__arrow" aria-label="Explore the Virtual Field Trip: White Bird - The Courage of Kindness">→</Link>
            </div>
          </Column>
        </Row>
      </Container>
    </Section>

    {/* ── Civic Activations banner ────────────────────────────────── */}
    <Link to="/what-is-testimony/" className="home-civic-banner">
      <span>NEW CIVIC ACTIVATIONS: Engage your students in ready to use quick starters on Empathy, Resilience and Courage!</span>
      <span className="home-civic-banner__arrow">→</span>
    </Link>

    {/* ── About ───────────────────────────────────────────────────── */}
    <Section className="home-about-section">
      <Container>
        <Row>
          <Column>
            <h2>About</h2>
            <p>
              USC Shoah Foundation and Discovery Education have partnered to
              create <em>Teaching with Testimony</em>, a new educational program
              that unlocks the powerful classroom potential of testimony. Drawing
              upon USC Shoah Foundation's vast library of audiovisual testimony
              from survivors and witnesses of genocides,{' '}
              <em>Teaching with Testimony empowers students</em> to find their
              voices and take action for a better future.
            </p>
          </Column>
        </Row>
      </Container>
    </Section>

    {/* ── Video Topic Series ──────────────────────────────────────── */}
    <Section>
      <Container>
        <h2 className="home-section-heading">Video Topic Series</h2>
        <Row className="home-feature-row">
          <Column size={5}>
            <Image filename="art-of-resilience.png" alt="The Art of Resilience" />
          </Column>
          <Column size={7}>
            <h3>The Art of Resilience</h3>
            <p>
              Celebrate the power that every student has to resist injustice in
              their world and contribute to a better future through the decisions
              and actions they make today.
            </p>
            <Button to="/video-topic-series/">Watch On-Demand</Button>
          </Column>
        </Row>
      </Container>
    </Section>

    {/* ── Virtual Field Trip ──────────────────────────────────────── */}
    <Section>
      <Container>
        <h2 className="home-section-heading">Virtual Field Trip</h2>
        <Row className="home-feature-row">
          <Column size={7}>
            <h3>White Bird: The Courage of Kindness</h3>
            <p>
              Explore the small acts of kindness that can change lives and
              communities for the better as students investigate real-world
              examples of courage in the face of adversity in the newest Virtual
              Field Trip.
            </p>
            <Button to="/virtual-field-trip/white-bird/">Watch On-Demand</Button>
          </Column>
          <Column size={5}>
            <Image
              filename="home-white-bird-vft.jpeg"
              alt="White Bird: The Courage of Kindness Virtual Field Trip"
            />
          </Column>
        </Row>
      </Container>
    </Section>

    {/* ── Teaching with Testimony ─────────────────────────────────── */}
    <Section className="home-about-section">
      <Container>
        <h2 className="home-section-heading">Teaching with Testimony</h2>
        <Row>
          <Column size={5}>
            <Image
              filename="home-what-is-testimony-thumnail.jpg"
              alt="What is Testimony?"
            />
          </Column>
          <Column size={7}>
            <p>
              Get started by sharing the <em>What is Testimony?</em> video with
              your class. Next, download the corresponding educator guide,
              designed to provide you with the foundations of{' '}
              <em>Teaching with Testimony.</em> Plus, take a look at the curated
              selection of video testimonies or explore the middle school and
              high school activities to learn more.
            </p>
            <Button to="/educator-resources/">Educator Resources</Button>
            <p className="mt-1">
              <Link to="/testimony/" className="home-text-link">
                Watch Testimonies →
              </Link>
            </p>
          </Column>
        </Row>
      </Container>
    </Section>

    {/* ── Why We Hate ─────────────────────────────────────────────── */}
    <Section>
      <Container>
        <h2 className="home-section-heading">Why We Hate</h2>
        <Row>
          <Column size={5}>
            <Video
              src="https://app.discoveryeducation.com/learn/embedded-player/be2046d5-b84d-4958-a6ce-7cc7245f3938?utm_source=microsite&utm_medium=embed"
              title="WHY WE HATE Selects: Origins"
            />
          </Column>
          <Column size={7}>
            <p>
              In collaboration with Discovery Channel's global premiere of WHY
              WE HATE, a six-part docuseries that explores the reasons why
              humans hate, USC Shoah Foundation and Discovery Education have
              created a collection of powerful instructional resources that
              connect the important themes of the docuseries to classroom
              instruction.
            </p>
            <Button to="/resources/why-we-hate/">Explore</Button>
          </Column>
        </Row>
      </Container>
    </Section>

    {/* ── Willesden Project ───────────────────────────────────────── */}
    <Section className="home-willesden-section">
      <Container>
        <Row>
          <Column size={4} className="home-willesden-logo-col">
            <Image
              filename="willesden-project-logo.png"
              alt="The Willesden Project"
              className="home-willesden-logo"
            />
          </Column>
          <Column size={8}>
            <p>
              The Willesden Project brings the power of music and story to reach
              young people globally and contribute to their development as
              empathetic, knowledgeable and resilient individuals.
            </p>
            <Button to="/resources/willesden-project/">Learn More</Button>
          </Column>
        </Row>
      </Container>
    </Section>


  </Layout>
);

export default IndexPage;
